import { useQuery } from "@tanstack/react-query";
import { orgSettingsFromRows, type OrgConfig } from "@talentkontor/domain";
import { api } from "../data";
import { EinrichtungFehlt, retryAusserEinrichtung } from "../lib/einrichtung";
import { useNurLesen } from "../app/nurLesen";

/**
 * 0425 (S-6b): Ersatzwerte zum Lesen, wenn eine Agentur in der Pause bzw. im Nur-Lesen-Zustand nicht fertig eingerichtet ist. Dann
 * lässt sich nichts ergänzen; die Seiten sollen trotzdem lesen können (nichts wird damit gerechnet oder gespeichert, Schreibwege
 * sind ohnehin gesperrt). Ohne Nur-Lesen gilt weiter: fehlt ein Pflichtwert, ist die Einrichtung offen (Probelauf H1).
 */
const ERSATZ_EINSTELLUNG: Record<string, unknown> = {
  timezone: "Europe/Berlin", locale: "de-DE", currency: "EUR", default_commission_bp: 0, payment_terms_days: 14, due_soon_days: 3,
  payment_check_cooldown_days: 7, still_overdue_recheck_days: 7, talent_answer_days: 7, min_line_description_chars: 0,
  invoice_date_workdays_only: false, budget_deviation_percent: 0, expense_keywords: [], signing_link_days: 14,
};
const ERSATZ_MAHNUNG: Record<string, unknown> = { interest_enabled: false, fee_mode: "highest", surcharge_bp: 0 };

/** Einstellungen einer Agentur als Fachlogik-Konfiguration (Plattform-Standards füllen Lücken). */
export async function loadOrgConfig(orgId: string, opts: { nurLesen?: boolean } = {}): Promise<OrgConfig> {
  const byOrg = [["org_id", "eq", orgId]] as const;
  const [settings, policy, steps, rates, defaults] = await Promise.all([
    api.select("org_settings", { filters: byOrg }),
    api.select("dunning_policies", { filters: byOrg }),
    api.select("dunning_steps", { filters: byOrg, order: [["step", "asc"]] }),
    api.select("dunning_base_rates", { filters: byOrg, order: [["valid_from", "asc"]] }),
    api.select<{ key: string; value: unknown }>("platform_defaults"),
  ]);
  const org: Record<string, unknown> = { ...((settings[0] as Record<string, unknown> | undefined) ?? {}) };
  const pol: Record<string, unknown> = { ...((policy[0] as Record<string, unknown> | undefined) ?? {}) };
  let stufen = steps as unknown[];
  // Probelauf H1: fehlt ein Pflichtwert (auch ohne Plattform-Standard), ist die Einrichtung offen, kein Seitenfehler
  for (let versuch = 0; ; versuch++) {
    try {
      return orgSettingsFromRows({
        org_settings: settings[0] || Object.keys(org).length ? org as never : null,
        dunning_policy: policy[0] || Object.keys(pol).length ? pol as never : null,
        dunning_steps: stufen as never,
        dunning_base_rates: rates as never,
        platform_defaults: Object.fromEntries(defaults.map((d) => [d.key, d.value])),
      });
    } catch (e) {
      const m = /^SETTING_MISSING: (.+)$/.exec(e instanceof Error ? e.message : "");
      const key = m?.[1];
      // 0425 (S-6b): in der Pause bzw. im Nur-Lesen-Zustand mit Ersatzwerten weiterlesen statt die Einrichtung zu verlangen
      if (opts.nurLesen && key && versuch < 20) {
        if (key in ERSATZ_EINSTELLUNG && org[key] == null) { org[key] = ERSATZ_EINSTELLUNG[key]; continue; }
        if (key in ERSATZ_MAHNUNG && pol[key] == null) { pol[key] = ERSATZ_MAHNUNG[key]; continue; }
        if (key === "dunning_steps" && !stufen.length) { stufen = [{ step: 1, key: "erinnerung", days: 7, day_type: "calendar", fee_cents: 0, interest: false, is_reminder: true }]; continue; }
      }
      throw key ? new EinrichtungFehlt(key) : e;
    }
  }
}

export function useOrgConfig(orgId: string) {
  const nurLesen = useNurLesen();
  return useQuery({ queryKey: nurLesen ? ["org_config", orgId, "lesen"] : ["org_config", orgId], queryFn: () => loadOrgConfig(orgId, { nurLesen }), staleTime: 5 * 60_000, retry: retryAusserEinrichtung });
}
